import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  OktonAlert,
  OktonInput,
  OktonLoadingState,
  OktonSelect,
} from "@okton/platform-ui";

import { AiInventoryPanel } from "./AiInventoryPanel";
import { AiConnection, AiSource, AiSourcesState } from "./types";
import {
  AiSourcesError,
  loadAiConnections,
  loadAiSources,
  saveAiSources,
} from "./aiSourcesApi";

interface Props {
  systemId: string;
  accessToken: string;
}

const SourcesForm: React.FC<Props> = ({ systemId, accessToken }) => {
  const [state, setState] = React.useState<AiSourcesState | null>(null);
  const [savedItems, setSavedItems] = React.useState("[]");
  const [connections, setConnections] = React.useState<AiConnection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [conflict, setConflict] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const savingRef = React.useRef(false);
  const activeController = React.useRef<AbortController | null>(null);

  const tokenRef = React.useRef(accessToken);
  tokenRef.current = accessToken;
  const hasSession = Boolean(accessToken);

  React.useEffect(() => {
    const token = tokenRef.current;
    const controller = new AbortController();
    activeController.current = controller;
    savingRef.current = false;
    setSaving(false);
    setLoading(true);
    setState(null);
    setError("");
    setMessage("");
    setConflict(false);
    if (!hasSession) {
      setError("Sessão indisponível. Entre novamente no SSO Admin.");
      setLoading(false);
      return () => controller.abort();
    }
    Promise.all([
      loadAiSources(systemId, token, controller.signal),
      loadAiConnections(systemId, token, controller.signal),
    ])
      .then(([sources, available]) => {
        if (controller.signal.aborted) return;
        setState(sources);
        setSavedItems(JSON.stringify(sources.items));
        setConnections(available);
      })
      .catch((failure: Error) => {
        if (!controller.signal.aborted)
          setError(failure.message || "Erro ao carregar fontes.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [systemId, hasSession, reload]);

  function patch(index: number, values: Partial<AiSource>): void {
    setState((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item, position) =>
              position === index ? { ...item, ...values } : item,
            ),
          }
        : current,
    );
    setMessage("");
  }

  function addSource(): void {
    const connection = connections[0];
    if (!connection) return;
    setState((current) =>
      current
        ? {
            ...current,
            items: [
              ...current.items,
              {
                label: "",
                kind: connection.kind,
                connectionId: connection.id,
                location: "",
                ref: connection.kind === "git" ? "main" : "",
                enabled: true,
              },
            ],
          }
        : current,
    );
    setMessage("");
  }

  async function save(): Promise<void> {
    const controller = activeController.current;
    if (
      !state ||
      !controller ||
      controller.signal.aborted ||
      savingRef.current ||
      conflict
    )
      return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const saved = await saveAiSources(
        systemId,
        accessToken,
        {
          revision: state.revision,
          items: state.items,
        },
        controller.signal,
      );
      if (!controller.signal.aborted) {
        setState(saved);
        setSavedItems(JSON.stringify(saved.items));
        setMessage("Fontes salvas.");
      }
    } catch (failure) {
      if (controller.signal.aborted) return;
      setError(
        failure instanceof Error ? failure.message : "Erro ao salvar fontes.",
      );
      if (failure instanceof AiSourcesError && failure.status === 409)
        setConflict(true);
    } finally {
      if (
        activeController.current === controller &&
        !controller.signal.aborted
      ) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <Box borderTopWidth="1px" pt="5">
      <Flex align="center" justify="space-between" wrap="wrap" mb="3">
        <Heading size="sm">Fontes para a IA Okton</Heading>
        <Badge colorScheme="gray">Somente leitura</Badge>
      </Flex>
      <Text fontSize="sm" color="gray.500" mb="4">
        Cadastre os repositórios ou pastas que documentam este sistema. Pastas
        remotas exigem um leitor autorizado no servidor de origem.
      </Text>
      {error && (
        <OktonAlert status="error" mb="3">
          <Text>{error}</Text>
        </OktonAlert>
      )}
      {message && (
        <OktonAlert status="success" mb="3">
          <Text>{message}</Text>
        </OktonAlert>
      )}
      {loading ? (
        <OktonLoadingState title="Carregando fontes..." minH="120px" />
      ) : (
        <>
          {state && (
            <Stack spacing="4">
              {connections.length === 0 && (
                <Text fontSize="sm">
                  Nenhuma conexão autorizada foi configurada para este sistema.
                  Solicite a configuração à equipe Okton.
                </Text>
              )}
              {state.items.map((source, index) => (
                <Box
                  key={source.id || "new-" + index}
                  borderWidth="1px"
                  rounded="md"
                  p="4"
                >
                  <SimpleGrid columns={[1, 2]} spacing="3">
                    <OktonInput
                      name={"source-name-" + index}
                      label="Nome da fonte"
                      required
                      aria-label={"Nome da fonte " + (index + 1)}
                      value={source.label}
                      maxLength={120}
                      isDisabled={saving}
                      onChange={(event) =>
                        patch(index, { label: event.target.value })
                      }
                    />
                    <OktonSelect
                      name={"source-connection-" + index}
                      label="Conexão autorizada"
                      required
                      value={source.connectionId}
                      isDisabled={saving}
                      onChange={(event) => {
                        const selected = connections.find(
                          (connection) => connection.id === event.target.value,
                        );
                        if (selected)
                          patch(index, {
                            connectionId: selected.id,
                            kind: selected.kind,
                            location: "",
                            ref: selected.kind === "git" ? "main" : "",
                          });
                      }}
                    >
                      {!connections.some(
                        (connection) => connection.id === source.connectionId,
                      ) && (
                        <option value={source.connectionId}>
                          Conexão indisponível
                        </option>
                      )}
                      {connections.map((connection) => (
                        <option key={connection.id} value={connection.id}>
                          {connection.label}
                        </option>
                      ))}
                    </OktonSelect>
                    <OktonInput
                      name={"source-location-" + index}
                      label={
                        source.kind === "git"
                          ? "Repositório HTTPS"
                          : "Caminho absoluto autorizado"
                      }
                      required
                      aria-label={"Local da fonte " + (index + 1)}
                      value={source.location}
                      maxLength={1000}
                      isDisabled={saving}
                      onChange={(event) =>
                        patch(index, { location: event.target.value })
                      }
                      placeholder={
                        source.kind === "git"
                          ? "https://github.com/organizacao/projeto.git"
                          : "/caminho/do/sistema"
                      }
                    />
                    {source.kind === "git" && (
                      <OktonInput
                        name={"source-ref-" + index}
                        label="Branch autorizada"
                        required
                        value={source.ref}
                        maxLength={200}
                        isDisabled={saving}
                        onChange={(event) =>
                          patch(index, { ref: event.target.value })
                        }
                      />
                    )}
                  </SimpleGrid>
                  <Flex mt="3" align="center" justify="space-between">
                    <Checkbox
                      isChecked={source.enabled}
                      isDisabled={saving}
                      onChange={(event) =>
                        patch(index, { enabled: event.target.checked })
                      }
                    >
                      Fonte habilitada
                    </Checkbox>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={"Remover fonte " + (index + 1)}
                      isDisabled={saving}
                      onClick={() =>
                        setState((current) =>
                          current
                            ? {
                                ...current,
                                items: current.items.filter(
                                  (_, position) => position !== index,
                                ),
                              }
                            : current,
                        )
                      }
                    >
                      Remover
                    </Button>
                  </Flex>
                </Box>
              ))}
              {state.items.length === 0 && (
                <Text fontSize="sm" color="gray.500">
                  Nenhuma fonte cadastrada.
                </Text>
              )}
              <Flex wrap="wrap" gap="3">
                <Button
                  size="sm"
                  onClick={addSource}
                  isDisabled={
                    saving ||
                    connections.length === 0 ||
                    state.items.length >= 50
                  }
                >
                  Adicionar fonte
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={save}
                  isLoading={saving}
                  isDisabled={conflict}
                >
                  Salvar fontes
                </Button>
              </Flex>
            </Stack>
          )}
          {(conflict || !state) && (
            <Box mt="3">
              {conflict && (
                <Text fontSize="sm" mb="2">
                  Sua edição foi mantida. Recarregar substitui os campos pela
                  versão salva no servidor.
                </Text>
              )}
              <Button
                size="sm"
                onClick={() => setReload((value) => value + 1)}
                isDisabled={saving}
              >
                Recarregar fontes
              </Button>
            </Box>
          )}
        </>
      )}
      <AiInventoryPanel
        systemId={systemId}
        accessToken={accessToken}
        sources={state}
        connections={connections}
        dirty={Boolean(state && JSON.stringify(state.items) !== savedItems)}
        busy={saving || loading || conflict}
      />
    </Box>
  );
};

export const AiSourcesEditor: React.FC<Props> = (props) => (
  <SourcesForm key={props.systemId} {...props} />
);
